import React, { useState } from 'react';
import './TeamSection.css';
import profileImage from '../assets/profile.jpg';
import abdullahImage from '../assets/abdullah-omar.jpg';
import logoEmblem from '../assets/logo-emblem.svg';

const BIO_PREVIEW_LENGTH = 300;

const teamMembers = [
  {
    name: 'Amir Ali, CFA',
    title: 'Founder and Principal Consultant',
    image: profileImage,
    linkedin: 'https://www.linkedin.com/in/amirali86',
    email: 'amir@clarityimpactfinance.com',
    bio: `I live in Rockland County, New York, where I serve on the County Planning Board, and I sit on ROC USA's loan review committee for resident-owned manufactured home communities. Before starting Clarity, I spent nine years at Low Income Investment Fund (LIIF), one of the nation's largest CDFIs, most recently as Vice President overseeing community facilities lending and New Markets Tax Credits. Today I advise CDFIs and other mission-driven lenders on lending and capital strategy, fund design, loan systems, and underwriting.

At Clarity, I've worked on more than a dozen projects in eight states so far. I built a loan participation strategy to help a regional CDFI lend more, designed a $100 million affordable housing debt fund, and created a new underwriting model for a national lender. I also help lenders set up loan origination and management systems, and I underwrite affordable housing and community facility loans.

At LIIF, I deployed more than $150 million in impact capital, structured and closed more than 25 NMTC transactions, and worked with CDFI clients ranging from $50 million to $900 million in total assets. Before LIIF, I spent about five years in institutional investing at the Alberta Teachers' Retirement Fund, a sovereign wealth fund in Abu Dhabi, and Aviva Investors, where I managed real estate private equity funds. Those two worlds think about capital and risk very differently, and most of what I do at Clarity comes down to translating between them. I hold a B.Com in Finance and Accounting from McGill University and am a CFA Charterholder.

Outside of work, I'm usually with my wife and our two young daughters, playing basketball or pickleball, hiking, or at CrossFit. As for the stress of the CDFI world, two toddlers at home have been excellent training.`,
  },
  {
    name: 'Abdullah Omar, MBA',
    title: 'Consultant, Strategic Advisory',
    image: abdullahImage,
    linkedin: 'https://www.linkedin.com/in/ao777/',
    email: 'omar@clarityimpactfinance.com',
    bio: `My perspective on community development finance has been shaped by experience across affordable housing development, CDFI treasury and asset management, and the financial and impact analysis of CDFIs nationwide. That range of experience informs how I approach strategic advisory at Clarity: connecting the details of individual transactions and lending programs to the broader financial and operational decisions that support sustainable growth.

I began my career at a public housing authority in the Denver metro area, supporting multifamily development projects and seeing firsthand how public, private, and philanthropic capital come together to build and preserve affordable housing. I later worked at Capital Impact Partners across treasury and asset management, gaining exposure to the capital and portfolio management functions of a national CDFI.

During nearly two years at Aeris Insight, I gained a sector-wide perspective on CDFIs through financial and impact analysis of institutions across the country. That experience sharpened my understanding of how financial strategy, portfolio performance, risk management, organizational capacity, and mission come together in practice.

Today, my work at Clarity spans both transactions and strategic advisory. I support NMTC and LIHTC transactions, underwrite charter schools, childcare centers, and other community facilities, and advise CDFIs on strengthening the credit, portfolio management, and operational systems behind their lending programs.

I hold an MBA from the Swiss School of Business and Management, a BA in Music Production and Sound Engineering, and a CAPM certification from the Project Management Institute. My background in music continues to shape the way I approach complex work, helping me balance close attention to detail with a broader sense of how the pieces fit together.`,
  },
];

const TeamMember = ({ member }) => {
  const [showFullBio, setShowFullBio] = useState(false);
  const hasLongBio = member.bio.length > BIO_PREVIEW_LENGTH;
  const bioText = showFullBio || !hasLongBio
    ? member.bio
    : member.bio.substring(0, BIO_PREVIEW_LENGTH) + '...';

  return (
    <div className="team-member">
      <div className="member-image-container">
        <img
          src={member.image}
          alt={`${member.name} - ${member.title}`}
          className="member-image"
        />
      </div>
      <div className="member-content">
        <h3>{member.name}</h3>
        <p className="member-title">{member.title}</p>
        <div className="member-bio">
          <p>{bioText}</p>
          {hasLongBio && (
            <button
              className="read-more-btn"
              onClick={() => setShowFullBio(!showFullBio)}
            >
              {showFullBio ? 'Show Less' : 'Read More'}
            </button>
          )}
        </div>
        <div className="member-social">
          <a
            href={member.linkedin}
            target="_blank"
            rel="noopener noreferrer"
            className="social-link linkedin"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
            </svg>
            LinkedIn
          </a>
          <a href={`mailto:${member.email}`} className="social-link email">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M0 3v18h24v-18h-24zm6.623 7.929l-4.623 5.712v-9.458l4.623 3.746zm-4.141-5.929h19.035l-9.517 7.713-9.518-7.713zm5.694 7.188l3.824 3.099 3.83-3.104 5.612 6.817h-18.779l5.513-6.812zm9.208-1.264l4.616-3.741v9.348l-4.616-5.607z"/>
            </svg>
            Email
          </a>
        </div>
      </div>
    </div>
  );
};

const TeamSection = () => {
  return (
    <section id="about" className="about">
      <div className="about-content-wrapper">
        <div className="logo-emblem-container">
          <img src={logoEmblem} alt="Clarity Impact Finance Logo Emblem" className="logo-emblem" />
        </div>

        {/* Vision Section - Changed from Leadership Section */}
        <h2>Our Vision</h2>

        <div className="vision-statement">
          <p>Closing the gap between impact capital and the communities it's meant to serve.</p>
        </div>

        {/* Team Section Heading */}
        <h3 className="section-subheading team-heading">Our Team</h3>

        <div className="team-section">
          {teamMembers.map((member) => (
            <TeamMember key={member.name} member={member} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default TeamSection;
